import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Pagination, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createVirtualIpProject,
  listVirtualIpProjects,
  type SaasApiError,
  type VirtualIpProjectSummary,
} from '../../api'
import { virtualIpProjectBase } from './virtualIpPaths'
import './virtualIp.css'
import '../../ui/tenant-items.css'
import '../contentProduction/content-production.css'

const PAGE_SIZE = 16

function statusLabel(status: string, t: (k: string) => string): string {
  if (status === 'completed') return t('ia.projectStatus.completed')
  if (status === 'generating') return t('ia.projectStatus.generating')
  if (status === 'positioning') return t('ia.projectStatus.positioning')
  return t('ia.projectStatus.draft')
}

function formatUpdatedAt(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export default function VirtualIpProjectListPage() {
  const { t } = useTranslation('virtualIp')
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<VirtualIpProjectSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q])

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    try {
      const { data } = await listVirtualIpProjects(workspaceId, {
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      message.error((e as SaasApiError).message || t('ia.loadProjectsFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, debouncedQ, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const onCreate = async () => {
    if (!workspaceId) return
    setCreating(true)
    try {
      const { data } = await createVirtualIpProject(workspaceId)
      message.success(t('ia.createProjectSuccess'))
      navigate(`${virtualIpProjectBase(workspaceId, data.id)}/step-1`)
    } catch (e) {
      message.error((e as SaasApiError).message || t('ia.createProjectFailed'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="tc-items-page cpg-page" data-testid="virtual-ip-project-list">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('ia.projectsTitle')}</h2>
          <p className="tc-page-desc">{t('ia.projectsSubtitle')}</p>
        </div>
        <div className="tc-page-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={creating}
            onClick={() => void onCreate()}
            data-testid="virtual-ip-project-create"
          >
            {t('ia.newProject')}
          </Button>
        </div>
      </div>

      <div className="tc-data-card">
        <div className="cpg-list-toolbar">
          <div className="cpg-list-toolbar-search">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('ia.searchProjects')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 260 }}
              data-testid="virtual-ip-project-search"
            />
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="cpg-gallery-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="cpg-card cpg-card--skeleton">
                <Skeleton.Image active style={{ width: '100%', height: 180 }} />
                <Skeleton active paragraph={{ rows: 1 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Empty
            description={t('ia.emptyProjects')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => void onCreate()}>
              {t('ia.newProject')}
            </Button>
          </Empty>
        ) : (
          <>
            <div className="cpg-gallery-grid" data-testid="virtual-ip-project-grid">
              {items.map((project) => (
                <article
                  key={project.id}
                  className="cpg-card vip-ia-card"
                  role="button"
                  tabIndex={0}
                  data-testid={`virtual-ip-project-card-${project.id}`}
                  onClick={() => navigate(virtualIpProjectBase(workspaceId, project.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(virtualIpProjectBase(workspaceId, project.id))
                    }
                  }}
                >
                  <div className="cpg-card-visual vip-ia-card-visual">
                    {project.cover_url ? (
                      <img src={project.cover_url} alt="" className="vip-ia-card-cover" />
                    ) : (
                      <div className="vip-ia-card-placeholder">{t('ia.coverPlaceholder')}</div>
                    )}
                    <div className="cpg-card-hover-cta">{t('ia.openProject')}</div>
                  </div>
                  <div className="cpg-card-footer vip-ia-card-footer">
                    <div className="vip-ia-card-meta">
                      <span className="cpg-card-footer-title" title={project.name}>
                        {project.name}
                      </span>
                      <div className="vip-ia-card-sub">
                        <Tag>{statusLabel(project.status, t)}</Tag>
                        <span className="vip-ia-card-updated">{formatUpdatedAt(project.updated_at)}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {total > PAGE_SIZE ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Pagination
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
